import { useState } from 'react';
import { Plus } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { SelectNativo } from '@/components/ui/select-nativo';
import { Aviso } from '@/components/ui/aviso';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { useToast } from '@/lib/hooks/use-toast';
import { mensajeDeError } from '@/lib/api/mensaje-de-error';
import { prixiaService } from '../services/prixia-service';
import type { EjeDeTiendas, FilaDeTiendaPrixia } from '../types/prixia.types';

interface Props {
  ejes: EjeDeTiendas[];
  tiendas: FilaDeTiendaPrixia[];
  /** Después de cada cambio: la página vuelve a leer los rubros. */
  onCambio: () => void;
}

/** Un rubro prendido sin ninguna tienda leída: la invitación ya no lo ofrece. */
export const sinTiendasLeidas = (e: EjeDeTiendas) => e.activo && e.tiendasLeidas === 0;

/**
 * Los rubros de las tiendas de Prixia (reglas de la demo, 343). La invitación de la prueba ofrece
 * sólo los rubros prendidos que tienen al menos una tienda leída; cada rubro puede tener una
 * «Sugerida», que es la que sale primera en «De tu rubro». Nada se borra: apagar un rubro lo
 * saca de la invitación, y el servidor lo frena si todavía tiene tiendas o empresas en prueba.
 */
export function RubrosDeTiendasPrixia({ ejes, tiendas, onCambio }: Props) {
  const { toast } = useToast();
  const [nuevo, setNuevo] = useState('');
  const [ocupado, setOcupado] = useState<string | null>(null);
  const [editando, setEditando] = useState<{ id: string; nombre: string } | null>(null);

  const sinLeer = ejes.filter(sinTiendasLeidas);

  const cambiar = async (clave: string, hacer: () => Promise<unknown>, porDefecto: string) => {
    setOcupado(clave);
    try {
      await hacer();
      onCambio();
      return true;
    } catch (err) {
      // El servidor dice el motivo en palabras (rubro repetido, rubro en uso, sugerida de otro).
      toast({
        variant: 'destructive',
        title: 'No se pudo',
        description: mensajeDeError(err, porDefecto),
      });
      return false;
    } finally {
      setOcupado(null);
    }
  };

  const agregar = async () => {
    const nombre = nuevo.trim();
    if (nombre.length < 2) return;
    const ok = await cambiar(
      'nuevo',
      () => prixiaService.crearEje({ nombre }),
      'No se pudo agregar.'
    );
    if (ok) {
      setNuevo('');
      toast({ title: `Rubro «${nombre}» agregado` });
    }
  };

  const renombrar = async () => {
    if (!editando) return;
    const ok = await cambiar(
      editando.id,
      () => prixiaService.editarEje(editando.id, { nombre: editando.nombre.trim() }),
      'No se pudo renombrar.'
    );
    if (ok) setEditando(null);
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg">Rubros de las tiendas</CardTitle>
        <p className="text-sm text-muted-foreground">
          La invitación de la prueba ofrece sólo los rubros prendidos que tienen al menos una tienda
          leída (con 20 artículos o más). La «Sugerida» sale primera dentro de su rubro.
        </p>
      </CardHeader>
      <CardContent className="space-y-4 p-0">
        {sinLeer.length > 0 && (
          <div className="px-4">
            <Aviso tono="alerta" titulo="Rubro sin tiendas leídas">
              {sinLeer.length === 1
                ? `«${sinLeer[0].nombre}» no tiene ninguna tienda leída: la invitación ya no lo ofrece. Leé una tienda de ese rubro o asignale una de la lista.`
                : `${sinLeer.map((e) => `«${e.nombre}»`).join(', ')} no tienen ninguna tienda leída: la invitación ya no los ofrece. Leé una tienda de cada rubro o asignale una de la lista.`}
            </Aviso>
          </div>
        )}

        {ejes.length === 0 ? (
          <p className="p-6 text-center text-sm text-muted-foreground">
            Todavía no hay rubros. Agregá el primero abajo.
          </p>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Rubro</TableHead>
                <TableHead>En la invitación</TableHead>
                <TableHead className="text-right">Tiendas</TableHead>
                <TableHead>Sugerida</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {ejes.map((e) => {
                const delRubro = tiendas.filter((t) => t.ejeId === e.id);
                const trabado = ocupado === e.id;
                return (
                  <TableRow key={e.id}>
                    <TableCell>
                      {editando?.id === e.id ? (
                        <form
                          className="flex items-center gap-2"
                          onSubmit={(ev) => {
                            ev.preventDefault();
                            void renombrar();
                          }}
                        >
                          <Input
                            aria-label={`Nuevo nombre de ${e.nombre}`}
                            className="h-8 w-44"
                            value={editando.nombre}
                            onChange={(ev) => setEditando({ id: e.id, nombre: ev.target.value })}
                            autoFocus
                          />
                          <Button
                            type="submit"
                            size="sm"
                            disabled={trabado || editando.nombre.trim().length < 2}
                          >
                            Guardar
                          </Button>
                          <Button
                            type="button"
                            size="sm"
                            variant="outline"
                            onClick={() => setEditando(null)}
                          >
                            Cancelar
                          </Button>
                        </form>
                      ) : (
                        <div className="flex items-center gap-2">
                          <span className="font-medium">{e.nombre}</span>
                          <Button
                            type="button"
                            size="sm"
                            variant="ghost"
                            onClick={() => setEditando({ id: e.id, nombre: e.nombre })}
                          >
                            Renombrar
                          </Button>
                        </div>
                      )}
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-wrap items-center gap-2">
                        <Switch
                          aria-label={`${e.nombre} en la invitación`}
                          checked={e.activo}
                          disabled={trabado}
                          onCheckedChange={(activo) =>
                            void cambiar(
                              e.id,
                              () => prixiaService.editarEje(e.id, { activo }),
                              'No se pudo cambiar el rubro.'
                            )
                          }
                        />
                        <span className="text-sm">{e.activo ? 'Prendido' : 'Apagado'}</span>
                        {sinTiendasLeidas(e) && <Badge variant="warning">Sin tiendas leídas</Badge>}
                      </div>
                    </TableCell>
                    <TableCell className="text-right tabular-nums">
                      {e.tiendas}
                      <span className="block text-xs text-muted-foreground">
                        {`${e.tiendasLeidas} ${e.tiendasLeidas === 1 ? 'leída' : 'leídas'}`}
                      </span>
                    </TableCell>
                    <TableCell>
                      <SelectNativo
                        aria-label={`Tienda sugerida de ${e.nombre}`}
                        className="w-56"
                        value={e.tiendaSugeridaId ?? ''}
                        disabled={trabado || delRubro.length === 0}
                        onChange={(ev) =>
                          void cambiar(
                            e.id,
                            () =>
                              prixiaService.editarEje(e.id, {
                                tiendaSugeridaId: ev.target.value || null,
                              }),
                            'No se pudo cambiar la sugerida.'
                          )
                        }
                      >
                        <option value="">Ninguna</option>
                        {delRubro.map((t) => (
                          <option key={t.id} value={t.id}>
                            {t.name}
                          </option>
                        ))}
                      </SelectNativo>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}

        <form
          className="flex flex-wrap items-center gap-2 px-4 pb-4"
          onSubmit={(ev) => {
            ev.preventDefault();
            void agregar();
          }}
        >
          <Input
            aria-label="Nombre del rubro nuevo"
            className="w-60"
            placeholder="Nombre del rubro nuevo"
            value={nuevo}
            onChange={(ev) => setNuevo(ev.target.value)}
          />
          <Button
            type="submit"
            variant="outline"
            disabled={ocupado === 'nuevo' || nuevo.trim().length < 2}
          >
            <Plus className="mr-2 h-4 w-4" aria-hidden="true" />
            Agregar rubro
          </Button>
        </form>
      </CardContent>
    </Card>
  );
}

export default RubrosDeTiendasPrixia;
